import React, { useState } from "react";
import { PageHero, DraftNotice } from "@/components/common/SectionHeading";
import { Download } from "lucide-react";
import ProgramSelect from "@/components/ProgramSelect";
import { submitTuitionLead } from "@/lib/lnlApi";
import { toast } from "sonner";

export default function Tuition() {
    const [form, setForm] = useState({ full_name: "", mobile: "", email: "", applicant_type: "", preferred_program: "", municipality: "", consent: false, request_type: "guide" });
    const [ok, setOk] = useState(null);
    async function submit(e) {
        e.preventDefault();
        if (!form.consent) return toast.error("Please give privacy consent.");
        try { const r = await submitTuitionLead(form); setOk(r.reference); toast.success("Request received"); }
        catch { toast.error("Could not send request."); }
    }
    return (
        <div data-testid="tuition-page">
            <PageHero
                eyebrow="Tuition & scholarships"
                title="Plan your education at LNL"
                description="Personalized tuition assessments, payment guidance, scholarships and vouchers. We do not publish invented figures — Admissions will share the current details for your applicant path."
                breadcrumbs={[{ label: "Home", to: "/" }, { label: "Tuition & Scholarships" }]}
            />
            <section className="py-10 bg-white">
                <div className="lnl-container grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div>
                        <DraftNotice tone="warn">Contact LNL Admissions for the latest tuition assessment and payment information.</DraftNotice>
                        <div className="mt-6 space-y-3 text-sm text-lnl-charcoal">
                            {[
                                ["Tuition information request", "Ask Admissions for the personalized breakdown that applies to your program and grade level."],
                                ["Payment guidance", "Learn about accepted payment options once your assessment is issued."],
                                ["Scholarships", "Learn about available scholarships and how to qualify."],
                                ["Vouchers & assistance", "Understand government voucher and financial-assistance programs applicable to your applicant type."],
                            ].map(([t, d]) => (
                                <div key={t} className="lnl-card p-4">
                                    <div className="font-heading font-semibold">{t}</div>
                                    <div className="text-lnl-gray mt-1">{d}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div id="scholarships">
                        <form onSubmit={submit} className="lnl-card p-6 bg-lnl-green-bg/40 border-lnl-green/20" data-testid="tuition-guide-form">
                            <div className="lnl-eyebrow">Download LNL Tuition & Scholarship Guide</div>
                            <div className="font-heading font-bold text-xl text-lnl-charcoal mt-1">Request the guide</div>
                            <div className="text-sm text-lnl-gray mt-1">The downloadable guide is provided by Admissions after a short form. This confirms your consent to be contacted.</div>
                            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {[
                                    ["full_name", "Full name"], ["mobile", "Mobile"], ["email", "Email"],
                                    ["applicant_type", "Applicant type"], ["municipality", "Municipality"],
                                ].map(([k, l]) => (
                                    <div key={k} className={k === "full_name" ? "sm:col-span-2" : ""}>
                                        <label className="text-xs text-lnl-gray font-heading font-semibold">{l}</label>
                                        <input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1 w-full rounded-md border border-lnl-border bg-white px-3 py-2.5" data-testid={`guide-${k}`} />
                                    </div>
                                ))}
                                <div className="sm:col-span-2">
                                    <label className="text-xs text-lnl-gray font-heading font-semibold">Preferred program</label>
                                    <ProgramSelect value={form.preferred_program} onChange={(v) => setForm({ ...form, preferred_program: v })} className="mt-1 w-full rounded-md border border-lnl-border bg-white px-3 py-2.5" testid="guide-preferred_program" />
                                </div>
                            </div>
                            <label className="mt-3 flex items-start gap-2 text-xs text-lnl-gray">
                                <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} data-testid="guide-consent" />
                                I consent to be contacted by LNL Admissions.
                            </label>
                            <button className="mt-4 lnl-btn-primary" data-testid="guide-submit"><Download className="h-4 w-4" /> Request Guide</button>
                            {ok && <div className="mt-3 text-xs text-lnl-green-deep">Request received. Reference: <b>{ok}</b>. Admissions will follow up.</div>}
                        </form>
                    </div>
                </div>
            </section>
        </div>
    );
}
